import { type ReactNode } from 'react';
import { ArrowRight, X } from 'lucide-react';

import { Badge } from '@/components/Badge/Badge';
import { Button } from '@/components/Button/Button';
import { FILTER_COPY } from '@/components/FilterBar/filterCopy';
import {
    activeFilterFields,
    type AppliedFilters,
    filterChips,
    type FilterGroup,
} from '@/components/FilterBar/filters';
import { cn } from '@/util/utils';

export interface FilterBarProps<K extends string> {
    groups: readonly FilterGroup<K>[];
    applied: AppliedFilters<K>;
    /**
     * Removing a chip refilters at once, unlike the drawer, which waits for Apply. A multi
     * field draws one chip per ticked value and names it here, so taking one location off
     * leaves the others on; anything else clears the field.
     */
    onRemove: (key: K, value?: string) => void;
    onOpen: () => void;
    /** A count after the chips, such as "Showing 3 of 42 lookbooks". */
    summary?: ReactNode;
    /**
     * Offered as Clear all beside Select filter when there is something to clear. The screen
     * decides what that is, since a register's own search is cleared with its filters.
     */
    onClearAll?: () => void;
    className?: string;
}

/**
 * The design's filter bar (`.ord-filterbar`), sitting above the register's card: the label, the
 * applied filters as removable chips or the sentence that says there are none, the active count,
 * and Select filter opening the drawer. The bar shows state and removes it; everything else is
 * set in the drawer.
 */
export function FilterBar<K extends string>({
    groups,
    applied,
    onRemove,
    onOpen,
    summary,
    onClearAll,
    className,
}: FilterBarProps<K>) {
    const active = activeFilterFields(groups, applied);
    const chips = active.flatMap((field) =>
        filterChips(field, applied[field.key], applied).map((chip) => ({ field, ...chip })),
    );
    return (
        <div
            // The bar is a band of controls with no landmark of its own, and its own label is
            // a span rather than an accessible name, so the suite reaches it by test id.
            data-testid="filter-bar"
            className={cn(
                'border-border bg-card flex flex-wrap items-center gap-3 rounded-md border p-5',
                className,
            )}
        >
            <span className="text-label-lg text-foreground">{FILTER_COPY.label}</span>

            {/* The count sits by the label, before the chips it counts, as the design draws it. */}
            {active.length > 0 ? (
                <Badge variant="secondary" size="sm">
                    {FILTER_COPY.active(active.length)}
                </Badge>
            ) : null}

            {chips.length === 0 ? (
                <span className="text-muted-foreground text-sm">{FILTER_COPY.none}</span>
            ) : (
                <div className="flex min-w-0 flex-wrap gap-1.5">
                    {chips.map(({ field, value, text, pending }) => (
                        // The design's `.cmt-file--remove` pill: recessed ground, hairline, 12px muted
                        // text, and the whole chip darkens on hover to say it can go.
                        <button
                            key={`${field.key}:${value}`}
                            type="button"
                            aria-label={
                                pending
                                    ? FILTER_COPY.removePending(field.chipLabel)
                                    : FILTER_COPY.remove(text)
                            }
                            onClick={() => onRemove(field.key, value)}
                            className={cn(
                                'border-border bg-muted text-muted-foreground inline-flex items-center gap-[5px] rounded-full border px-[9px] py-0.5 text-xs leading-5 outline-none',
                                'hover:border-foreground hover:text-foreground focus-visible:ring-outline focus-visible:ring-[3px]',
                                'transition-colors duration-(--duration-fast) pointer-coarse:min-h-11',
                            )}
                        >
                            <span className="truncate">{text}</span>
                            <X className="size-3 shrink-0" aria-hidden />
                        </button>
                    ))}
                </div>
            )}

            {summary ? <span className="text-muted-foreground text-sm">{summary}</span> : null}

            {/* The label is not a control: Select filter is the only way into the drawer. */}
            <span className="ml-auto" />
            {onClearAll ? (
                <Button variant="ghost" size="sm" onClick={onClearAll}>
                    {FILTER_COPY.clear}
                </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={onOpen}>
                {FILTER_COPY.select}
                <ArrowRight />
            </Button>
        </div>
    );
}
